import React, { useRef, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  return `${API_URL}${url}`;
};

const getLogoShapeStyle = (shape) => {
  switch (shape) {
    case 'square': return { borderRadius: '12%' };
    case 'rounded-rect': return { borderRadius: '16%', aspectRatio: '1.4/1' };
    case 'circle': default: return { borderRadius: '50%' };
  }
};

const DESIGN_WIDTH = 500; // reference design width in px

const DesignCardPreview = ({ design, side = 'front', className = '', onClick }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        setScale(w / DESIGN_WIDTH);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const td = design.template_data || {};
  const bgColor = side === 'front' ? (td.front_bg_color || '#1e293b') : (td.back_bg_color || '#1e293b');
  const bgImage = side === 'front' ? td.front_bg_image : td.back_bg_image;
  const resolvedBg = resolveUrl(bgImage);
  const textElements = side === 'front' ? (design.front_text_elements || []) : (design.back_text_elements || []);
  const logoPos = side === 'front' ? td.front_logo_position : td.back_logo_position;
  const qrPos = side === 'back' ? td.back_qr_position : null;
  const userLogo = design.user_logo;

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden select-none ${className}`}
      style={{
        aspectRatio: '3.375 / 2.125',
        backgroundColor: bgColor,
        backgroundImage: resolvedBg ? `url(${resolvedBg})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
      data-testid={`design-preview-${side}`}
    >
      {/* Logo */}
      {logoPos && (() => {
        const displayLogo = (side === 'front' && userLogo) ? userLogo : resolveUrl(logoPos.image);
        if (displayLogo) {
          return (
            <div className="absolute overflow-hidden" style={{
              left: `${logoPos.x}%`, top: `${logoPos.y}%`,
              transform: 'translate(-50%, -50%)', width: `${logoPos.size}%`,
              ...getLogoShapeStyle(logoPos.shape || 'circle'),
            }}>
              <img src={displayLogo} alt="" className="w-full h-full object-cover" draggable={false}
                style={getLogoShapeStyle(logoPos.shape || 'circle')} />
            </div>
          );
        }
        if (!displayLogo) {
          return (
            <div className="absolute flex items-center justify-center" style={{
              left: `${logoPos.x}%`, top: `${logoPos.y}%`,
              width: `${logoPos.size}%`,
              ...(logoPos.shape === 'rounded-rect' ? { aspectRatio: '1.4/1' } : { aspectRatio: '1' }),
              transform: 'translate(-50%, -50%)',
              border: '1px dashed rgba(212,175,55,0.3)',
              ...getLogoShapeStyle(logoPos.shape || 'circle'),
            }}>
              <span style={{ fontSize: `${Math.max(6, 8 * scale)}px` }} className="text-amber-500/40 uppercase">Logo</span>
            </div>
          );
        }
        return null;
      })()}

      {/* Text elements - scaled to container width */}
      {textElements.map((el, i) => (
        <div key={i} className="absolute" style={{
          left: `${el.x}%`, top: `${el.y}%`,
          transform: 'translate(-50%, -50%)',
          fontSize: `${el.fontSize * scale}pt`, fontFamily: el.fontFamily || 'Inter',
          fontWeight: el.fontWeight || 'normal', color: el.color || '#FFFFFF',
          textAlign: el.textAlign || 'center', whiteSpace: 'nowrap',
          lineHeight: 1.2,
        }}>{el.text}</div>
      ))}

      {/* QR placeholder on back */}
      {side === 'back' && qrPos && (
        <div className="absolute flex items-center justify-center" style={{
          left: `${qrPos.x}%`, top: `${qrPos.y}%`,
          transform: 'translate(-50%, -50%)',
          width: `${qrPos.size}%`, aspectRatio: '1',
          border: `1px dashed ${(qrPos.color || '#fff')}50`,
          borderRadius: '6px',
        }}>
          <svg viewBox="0 0 24 24" className="w-1/2 h-1/2" fill="none" stroke={qrPos.color || '#fff'} strokeWidth="1.5" style={{ opacity: 0.4 }}>
            <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default DesignCardPreview;
